import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatApiError } from "../../lib/api";
import { StatusPill } from "./OverviewTab";

const SERVICES = ["Web Development", "Performance Marketing", "Brand Identity", "UI/UX Engineering", "SEO & Growth"];
const BUDGETS = ["₹25k – ₹75k", "₹75k – ₹2L", "₹2L – ₹10L", "₹10L+"];
const TIMELINES = ["ASAP", "1 – 2 months", "3 – 6 months", "Flexible"];
const FLOW = ["Pending", "In Review", "Proposal Sent", "In Development", "Completed"];

const inputCls =
  "w-full bg-obsidian border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt/60 transition-colors duration-300";

export default function BriefsTab() {
  const [enquiries, setEnquiries] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", service: SERVICES[0], budget: BUDGETS[0], timeline: TIMELINES[1], details: "" });

  const load = () => api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Brief submitted. Our strategists are on it.");
      setOpen(false);
      setForm({ title: "", service: SERVICES[0], budget: BUDGETS[0], timeline: TIMELINES[1], details: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="briefs-tab">
      <div className="flex items-center justify-between mb-8">
        <h3 className="font-display font-semibold text-xl tracking-tight">Project Briefs</h3>
        <button data-testid="new-enquiry-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-volt text-obsidian font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-3 hover:bg-[#CCEE00] transition-colors duration-300">
          <Plus size={14} /> New Brief
        </button>
      </div>

      {enquiries.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-sm text-slate-500">
          Nothing here yet. Your briefs and their live status will appear in this space.
        </div>
      ) : (
        <div className="space-y-4">
          {enquiries.map((e) => (
            <article key={e.id} data-testid={`enquiry-item-${e.id.slice(0, 8)}`} className="border border-white/5 bg-panel/40 p-6 lg:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h4 className="font-display font-semibold text-lg tracking-tight">{e.title}</h4>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {e.service} · {e.budget} · {e.timeline}
                  </p>
                </div>
                <StatusPill status={e.status} />
              </div>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-3xl">{e.details}</p>
              <div className="mt-6 flex items-center gap-0 overflow-x-auto">
                {FLOW.map((s, i) => {
                  const reached = FLOW.indexOf(e.status) >= i;
                  return (
                    <div key={s} className="flex items-center">
                      <span className={`font-mono text-[9px] uppercase tracking-[0.15em] whitespace-nowrap px-2 py-1 ${reached ? "text-volt" : "text-slate-600"}`}>{s}</span>
                      {i < FLOW.length - 1 && <span className={`w-6 h-px ${FLOW.indexOf(e.status) > i ? "bg-volt/60" : "bg-white/10"}`} />}
                    </div>
                  );
                })}
              </div>
              {e.admin_note && (
                <p className="mt-4 border-l-2 border-volt/60 pl-4 text-sm text-slate-300">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-volt block mb-1">Note from Zork</span>
                  {e.admin_note}
                </p>
              )}
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-panel border-white/10 text-slate-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">New Project Brief</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} data-testid="submit-enquiry-form" className="space-y-4 mt-2">
            <input data-testid="enquiry-title-input" required minLength={3} placeholder="Project title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select data-testid="enquiry-service-select" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={inputCls}>
                {SERVICES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <select data-testid="enquiry-budget-select" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className={inputCls}>
                {BUDGETS.map((b) => <option key={b}>{b}</option>)}
              </select>
              <select data-testid="enquiry-timeline-select" value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} className={inputCls}>
                {TIMELINES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <textarea data-testid="enquiry-details-input" rows={4} required placeholder="Objectives, scope, links — give us the full picture." value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className={`${inputCls} resize-none`} />
            <button data-testid="enquiry-submit-btn" disabled={busy} className="w-full bg-volt text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-[#CCEE00] transition-colors duration-300 disabled:opacity-50">
              {busy ? "Submitting..." : "Submit Brief"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
